import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Flex, HStack, Link, List, Skeleton, Stack, Text, Timeline, useBreakpointValue, } from '@chakra-ui/react';
import { OrderItemCard } from '@client/components/cards/OrderItemCard';
import { InfoPopover } from '@client/components/misc/InfoPopover';
import { Layout } from '@client/constants';
import { useOrderItemCardLayout } from '@client/hooks/useOrderItemCardLayout';
import { displayFontFamily, sansFontFamily } from '@client/theme';
import { formatDateLong } from '@client/utils/dateUtils';
import { OrderStatus } from '@heirloom/common/constants';
import { formatPaymentDetails } from '@heirloom/common/utils/paymentDisplay';
import { formatCentsAsDollars } from '@heirloom/common/utils/priceDisplay';
import { formatShippingAddress } from '@heirloom/common/utils/shippingAddress';
import { getShipmentTrackingUrl } from '@heirloom/common/utils/shippingTracking';
import { capitalize } from '@heirloom/common/utils/stringUtils';
import { FaCheck } from 'react-icons/fa';
import { FaBox, FaCcAmex, FaCcApplePay, FaCcDinersClub, FaCcDiscover, FaCcJcb, FaCcMastercard, FaCcVisa, FaCreditCard, FaTruck, } from 'react-icons/fa6';
const ORDER_ITEM_SKELETON_COUNT = 1;
const ORDER_ITEM_SKELETON_HEIGHT_DESKTOP = 175;
const ORDER_ITEM_SKELETON_HEIGHT_MOBILE = 340;
const ORDER_ITEM_SKELETON_WIDTH_MOBILE = 300;
const TIMELINE_SKELETON_HEIGHT = 90;
const TIMELINE_STATUS_ICONS = {
    [OrderStatus.CONFIRMED]: FaCheck,
    [OrderStatus.SHIPPED]: FaTruck,
};
export const SectionHeading = ({ children, }) => (_jsx(Text, { fontWeight: 500, fontSize: 22, fontFamily: displayFontFamily, children: children }));
const ShipmentsPopoverContent = ({ shipments, }) => (_jsxs(Stack, { gap: 2, children: [_jsx(Text, { fontFamily: displayFontFamily, fontSize: 20, fontWeight: 500, children: "Tracking" }), _jsx(List.Root, { gap: 2, variant: "plain", children: shipments.map((shipment, i) => (_jsx(List.Item, { children: _jsxs(HStack, { gap: 1, children: [_jsx(List.Indicator, { asChild: true, children: _jsx(FaBox, {}) }), _jsx(Link, { href: getShipmentTrackingUrl(shipment), target: "_blank", fontFamily: sansFontFamily, fontSize: 16, children: shipment.tracking })] }, i) }))) })] }));
export const OrderDetailTimeline = ({ timeline, shipments, }) => {
    if (timeline.length === 0)
        return null;
    return (_jsx(Timeline.Root, { size: "xl", variant: "outline", children: timeline.map((entry, i) => {
            const Icon = TIMELINE_STATUS_ICONS[entry.status];
            return (_jsxs(Timeline.Item, { children: [_jsxs(Timeline.Connector, { children: [_jsx(Timeline.Separator, {}), _jsx(Timeline.Indicator, { children: Icon && _jsx(Icon, { size: 16 }) })] }), _jsxs(Timeline.Content, { children: [_jsxs(Timeline.Title, { fontSize: 22, display: "flex", alignItems: "center", children: [capitalize(entry.status), entry.status ===
                                        OrderStatus.SHIPPED &&
                                        shipments.length > 0 && (_jsx(InfoPopover, { maxWidth: 375, children: _jsx(ShipmentsPopoverContent, { shipments: shipments }) }))] }), _jsxs(Timeline.Description, { fontSize: 20, children: [formatDateLong(entry.timestamp), entry.info && ` — ${entry.info}`] })] })] }, i));
        }) }));
};
export const OrderDetailTimelineSkeleton = () => (_jsx(Skeleton, { height: TIMELINE_SKELETON_HEIGHT, width: "100%" }));
export const OrderDetailItemsList = ({ items, }) => {
    const { layout, isCompact, cardProps } = useOrderItemCardLayout(items.length);
    const children = items.map((item, index) => (_jsx(OrderItemCard, { item: item, layout: layout, cardProps: cardProps, showQuantity: true }, index)));
    return isCompact ? (_jsx(HStack, { gap: 5, m: -5, p: 5, overflowX: "scroll", alignItems: "flex-start", children: children })) : (_jsx(Stack, { gap: 5, children: children }));
};
export const OrderDetailItemsListSkeleton = () => {
    const { isCompact } = useOrderItemCardLayout(ORDER_ITEM_SKELETON_COUNT);
    const children = Array.from({
        length: ORDER_ITEM_SKELETON_COUNT,
    }).map((_, i) => (_jsx(Skeleton, { borderRadius: "md", flexShrink: 0, height: isCompact
            ? ORDER_ITEM_SKELETON_HEIGHT_MOBILE
            : ORDER_ITEM_SKELETON_HEIGHT_DESKTOP, width: isCompact ? ORDER_ITEM_SKELETON_WIDTH_MOBILE : '100%' }, i)));
    return isCompact ? (_jsx(HStack, { gap: 5, m: -5, p: 5, overflowX: "scroll", alignItems: "flex-start", children: children })) : (_jsx(Stack, { gap: 5, children: children }));
};
export const OrderDetailSummaryAndShipping = ({ order, }) => {
    const layout = useBreakpointValue({
        base: Layout.MOBILE,
        md: Layout.DESKTOP,
    });
    const isMobile = layout === Layout.MOBILE;
    return (_jsxs(Flex, { direction: isMobile ? 'column' : 'row', gapX: 55, gapY: 5, alignItems: "start", fontSize: 18, children: [_jsxs(Stack, { width: isMobile ? '100%' : 200, gap: 1, children: [_jsx(SectionHeading, { children: "Summary" }), _jsxs(Stack, { gap: 0.5, children: [[
                                {
                                    label: 'Subtotal',
                                    value: formatCentsAsDollars(order.subtotalCents),
                                },
                                {
                                    label: 'Shipping',
                                    value: formatCentsAsDollars(order.shippingCents),
                                },
                                {
                                    label: 'Tax',
                                    value: formatCentsAsDollars(order.taxCents),
                                },
                            ].map(({ label, value }) => (_jsxs(HStack, { justifyContent: "space-between", children: [_jsx(Text, { children: label }), _jsx(Text, { fontSize: 20, children: value })] }, label))), _jsxs(HStack, { justifyContent: "space-between", fontWeight: 500, children: [_jsx(Text, { children: "Total" }), _jsx(Text, { fontSize: 20, children: formatCentsAsDollars(order.subtotalCents +
                                            order.shippingCents +
                                            order.taxCents) })] })] })] }), _jsxs(Stack, { gap: 1, children: [_jsx(SectionHeading, { children: "Ship To" }), _jsx(Text, { whiteSpace: "pre-line", lineHeight: 1.5, children: formatShippingAddress(order.shippingAddress) })] })] }));
};
export const OrderDetailSummaryAndShippingSkeleton = () => {
    const layout = useBreakpointValue({
        base: Layout.MOBILE,
        md: Layout.DESKTOP,
    });
    const isMobile = layout === Layout.MOBILE;
    return (_jsxs(Flex, { direction: isMobile ? 'column' : 'row', gapX: 55, gapY: 5, alignItems: "start", children: [_jsx(Skeleton, { flexShrink: 0, width: isMobile ? '100%' : 200, height: 175 }), _jsx(Skeleton, { flexShrink: 0, width: 180, height: 150 })] }));
};
const CARD_BRAND_ICONS = {
    visa: FaCcVisa,
    mastercard: FaCcMastercard,
    amex: FaCcAmex,
    discover: FaCcDiscover,
    diners: FaCcDinersClub,
    jcb: FaCcJcb,
};
const WALLET_ICONS = {
    apple_pay: FaCcApplePay,
};
const PaymentDetailsDisplay = ({ paymentDetails, }) => {
    const { cardBrand, walletType } = paymentDetails;
    const Icon = (walletType && WALLET_ICONS[walletType]) ||
        CARD_BRAND_ICONS[cardBrand] ||
        FaCreditCard;
    return (_jsxs(HStack, { fontSize: 20, children: [_jsx(Icon, { size: 22 }), _jsx(Text, { children: formatPaymentDetails(paymentDetails) })] }));
};
export const OrderDetailPaymentSection = ({ paymentDetails, }) => (_jsxs(Stack, { gap: 1, children: [_jsx(SectionHeading, { children: "Payment" }), _jsx(PaymentDetailsDisplay, { paymentDetails: paymentDetails })] }));
export const OrderDetailPaymentSectionSkeleton = () => (_jsx(Skeleton, { width: "100%", height: 75 }));
